"use client";

import { useState } from "react";
import Link from "next/link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, MoreHorizontal, Eye, UserPlus, MessageSquare } from "lucide-react";
import {
    mockComplaints,
    mockCategories,
    getStatusConfig,
    getPriorityConfig,
    ComplaintStatus,
} from "@/lib/mock-data";
import { formatDate, formatDateShort } from "@/lib/date-utils";

export default function ComplaintsListPage() {
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [categoryFilter, setCategoryFilter] = useState<string>("all");

    const filteredComplaints = mockComplaints.filter((complaint) => {
        const matchesSearch =
            complaint.referenceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            complaint.subject.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === "all" || complaint.status === statusFilter;
        const matchesCategory = categoryFilter === "all" || complaint.categoryId === categoryFilter;
        return matchesSearch && matchesStatus && matchesCategory;
    });

    const statuses: { value: string; label: string }[] = [
        { value: "all", label: "ทุกสถานะ" },
        { value: "submitted", label: "ยื่นเรื่อง" },
        { value: "received", label: "รับเรื่อง" },
        { value: "in_progress", label: "ดำเนินการ" },
        { value: "pending_info", label: "รอข้อมูล" },
        { value: "resolved", label: "แล้วเสร็จ" },
        { value: "closed", label: "ปิดเรื่อง" },
    ];

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="heading-sm text-primary">เรื่องร้องเรียนทั้งหมด</h1>
                    <p className="text-muted-foreground">จัดการและดำเนินการเรื่องร้องเรียน</p>
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="ค้นหาเลขที่อ้างอิง หรือ เรื่อง..."
                                className="pl-10"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-[180px]">
                                <SelectValue placeholder="สถานะ" />
                            </SelectTrigger>
                            <SelectContent>
                                {statuses.map((status) => (
                                    <SelectItem key={status.value} value={status.value}>
                                        {status.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                            <SelectTrigger className="w-full sm:w-[200px]">
                                <SelectValue placeholder="ประเภท" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">ทุกประเภท</SelectItem>
                                {mockCategories.map((cat) => (
                                    <SelectItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Complaints Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">รายการเรื่องร้องเรียน</CardTitle>
                    <CardDescription>พบ {filteredComplaints.length} รายการ</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>เลขที่อ้างอิง</TableHead>
                                    <TableHead>วันที่</TableHead>
                                    <TableHead className="min-w-[200px]">เรื่อง</TableHead>
                                    <TableHead>ประเภท</TableHead>
                                    <TableHead>สถานะ</TableHead>
                                    <TableHead>ความสำคัญ</TableHead>
                                    <TableHead>ผู้รับผิดชอบ</TableHead>
                                    <TableHead>SLA</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredComplaints.map((complaint) => {
                                    const statusConfig = getStatusConfig(complaint.status);
                                    const priorityConfig = getPriorityConfig(complaint.priority);
                                    const category = mockCategories.find((c) => c.id === complaint.categoryId);
                                    const isOverdue =
                                        new Date(complaint.dueDate) < new Date() &&
                                        !["resolved", "closed"].includes(complaint.status);

                                    return (
                                        <TableRow key={complaint.id}>
                                            <TableCell>
                                                <Link
                                                    href={`/staff/complaints/${complaint.id}`}
                                                    className="font-medium text-primary hover:underline"
                                                >
                                                    {complaint.referenceNumber}
                                                </Link>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground whitespace-nowrap">
                                                {formatDate(complaint.createdAt)}
                                            </TableCell>
                                            <TableCell>
                                                <p className="max-w-[200px] truncate" title={complaint.subject}>
                                                    {complaint.subject}
                                                </p>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                <span className="max-w-[120px] truncate block" title={category?.name}>
                                                    {category?.name.substring(0, 12)}...
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="secondary"
                                                    style={{
                                                        backgroundColor: statusConfig.bgColor,
                                                        color: statusConfig.darkColor,
                                                    }}
                                                >
                                                    {statusConfig.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    style={{
                                                        borderColor: priorityConfig.color,
                                                        color: priorityConfig.color,
                                                    }}
                                                >
                                                    {priorityConfig.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {complaint.assignedTo?.name.split(" ")[0] || (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {isOverdue ? (
                                                    <Badge variant="destructive">เกินกำหนด</Badge>
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">
                                                        {formatDateShort(complaint.dueDate)}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon">
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem asChild>
                                                            <Link href={`/staff/complaints/${complaint.id}`}>
                                                                <Eye className="mr-2 h-4 w-4" />
                                                                ดูรายละเอียด
                                                            </Link>
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem>
                                                            <UserPlus className="mr-2 h-4 w-4" />
                                                            มอบหมาย
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem>
                                                            <MessageSquare className="mr-2 h-4 w-4" />
                                                            ส่งข้อความ
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
